import React, { StrictMode, Suspense, lazy, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Frame } from "./screens/Frame";
import { AboutPage } from "./screens/About";
import { UGCOfferPage } from "./screens/LeadCapture";
import { NewInvestorsDeckV1 } from "./screens/InvestorPage";
import { MarketIntelligencePage } from "./screens/MarketIntelligence";
import { CrmProvidersLayout, CrmShell, FirstRunSetupPage, SchedulePage, CustomersPage, InventoryPage, NewCalendarDesignPage, ProductCatalogSetupPage, MarketingPage, MarketingDevPage } from "./screens/SalonCRM";

// The standalone staff screen was folded into the unified settings surface.
// Any legacy `/crm/staff` link now redirects to the Team section there.
const UNIFIED_TEAM_SETTINGS_PATH = "/crm/schedule?tab=settings&section=team";
import { SalonPerformanceDashboard } from "./screens/SalonPerformanceDashboard";
import { HomeDashboardPage } from "./screens/HomeDashboard";
import ReceptionFrontDeskPage from "./screens/FrontDeskHome/ReceptionFrontDeskPage";
import { AdminDashboard } from "./screens/AdminDashboard";
import { ProductDatabasePage } from "./screens/AdminDashboard/ProductDatabasePage";
import { ProductResolutionPage } from "./screens/AdminDashboard/ProductResolutionPage";
import { CustomerUsageIntelligencePage } from "./screens/CustomerUsageIntelligence";
import { PaulUsageFindingsPage } from "./screens/PaulUsageFindings";
import { LorealAnalyticsPage, SpectraIsraelMarketAnalyticsPage } from "./screens/LorealAnalytics";
import { HairGPTPage } from "./screens/HairGPT/HairGPTPage";
import { CompetitorsPage } from "./screens/Competitors";
import { TimelinePage } from "./screens/SpectraStory";
import { InternalRouteGate } from "./screens/SpectraStory/InternalRouteGate";
import { StrategicForecastPage } from "./screens/StrategicForecast";
import { MaorSpectraStoryPage } from "./screens/MaorSpectraStory";
import UserLoginPage from "./screens/UserLoginPage";
import RequireSalonSession from "./screens/SalonCRM/RequireSalonSession";

// Hidden investor experience — code-split so it never weighs the main bundle.
const SpectraProductVisionPage = lazy(() =>
  import("./screens/SpectraProductVision").then((m) => ({
    default: m.SpectraProductVisionPage,
  })),
);

// Dev-only asset intake / QA checklist for the investor experience.
const SpectraAssetCheckPage = lazy(() =>
  import("./screens/SpectraProductVision").then((m) => ({
    default: m.AssetCheckPage,
  })),
);

// New investor experience — hidden route, code-split, never weighs the main bundle.
const InvestorExperiencePage = lazy(() =>
  import("./screens/SpectraInvestorExperience").then((m) => ({
    default: m.InvestorExperiencePage,
  })),
);

// Salon AI-first investor narrative — hidden route, code-split.
const NewNarrativeSalonAIFirstPage = lazy(() =>
  import("./screens/NewNarrativeSalonAIFirst").then((m) => ({
    default: m.NewNarrativeSalonAIFirstPage,
  })),
);

const NewNarrativeSalonAIFirstWorkingPage = lazy(() =>
  import("./screens/NewNarrativeSalonAIFirst").then((m) => ({
    default: m.NewNarrativeSalonAIFirstWorkingPage,
  })),
);

const NewNarrativeSalonAIFirstLiveDemoDraftPage = lazy(() =>
  import("./screens/NewNarrativeSalonAIFirst").then((m) => ({
    default: m.NewNarrativeSalonAIFirstLiveDemoDraftPage,
  })),
);

// New Salon AI marketing homepage — isolated preview before replacing `/`.
const SalonAiHomePage = lazy(() =>
  import("./screens/SalonAiHome").then((m) => ({
    default: m.SalonAiHomePage,
  })),
);

const DemoBookingPage = lazy(() =>
  import("./screens/DemoBooking").then((m) => ({
    default: m.DemoBookingPage,
  })),
);

const StartNowPage = lazy(() =>
  import("./screens/StartNow").then((m) => ({
    default: m.StartNowPage,
  })),
);

const MarketplaceEditorialPreviewPage = lazy(() =>
  import("./screens/MarketplaceEditorialPreview").then((m) => ({
    default: m.MarketplaceEditorialPreviewPage,
  })),
);

// Existing-investor update — private editorial story, direct URL only.
const CurrentInvestorUpdatePage = lazy(() =>
  import("./screens/CurrentInvestorUpdate").then((m) => ({
    default: m.CurrentInvestorUpdatePage,
  })),
);

// September 2026 Founder Story Investor Edition — 19-slide deck.
const FounderStoryPresentationPage = lazy(() =>
  import("./screens/FounderStoryPresentation").then((m) => ({
    default: m.FounderStoryPresentationPage,
  })),
);

const FounderStoryShortPage = lazy(() =>
  import("./screens/FounderStoryShort").then((m) => ({
    default: m.FounderStoryShortPage,
  })),
);

// Color Intelligence Preview — private manufacturer preview, direct URL only.
const ColorIntelligencePreviewPage = lazy(() =>
  import("./screens/ColorIntelligencePreview").then((m) => ({
    default: m.ColorIntelligencePreviewPage,
  })),
);

// Israeli customer usage sample — private aggregated example, direct URL only.
const IsraelCustomerUsageExamplePage = lazy(() =>
  import("./screens/IsraelCustomerUsageExample").then((m) => ({
    default: m.IsraelCustomerUsageExamplePage,
  })),
);

// Advanced salon calendar mockup — standalone product demo, direct URL only.
const AdvancedSalonCalendarMockupPage = lazy(() =>
  import("./screens/AdvancedSalonCalendar").then((m) => ({
    default: m.AdvancedSalonCalendarMockupPage,
  })),
);

// GTM acquisition research — internal marketing tool, ships its own generated dataset.
const GTMResearchPage = lazy(() =>
  import("./screens/GTMResearch").then((m) => ({
    default: m.GTMResearchPage,
  })),
);

import "../tailwind.css";
import "./styles/critical.css";
import { PerformanceMonitor } from "./utils/performanceMonitor";
import { ToastProvider } from "./components/ui/toast";
import { NotificationProvider } from "./components/ui/notifications";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { LoadingSpinner } from "./components/LoadingSpinner";

function getPerfMonitor(): PerformanceMonitor | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const enable = Boolean((import.meta as any)?.env?.DEV) && params.get("debugPerf") === "1";
  if (!enable) return null;
  return new PerformanceMonitor();
}

const monitor = getPerfMonitor();
monitor?.startTiming("app-init");

// Page tracking component
function PageTracker() {
  const location = useLocation();

  useEffect(() => {
    monitor?.endTiming("app-init");
    console.log(`🌐 Navigated to: ${location.pathname}`);

    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("config", "GA_MEASUREMENT_ID", {
        page_path: location.pathname,
      });
    }
  }, [location]);

  return null;
}

/**
 * `overflow-x-hidden` looks harmless, but per the CSS overflow pairing rule an
 * unset overflow-y computes to `auto` whenever overflow-x isn't `visible` —
 * silently turning this shell into a scroll container. That breaks
 * `position: sticky` for every descendant that expects the *document* to be
 * the scrolling box (the investor deck's pinned sections rely on exactly
 * that). `overflow-x: clip` clips the same way without that side effect, so
 * it's scoped to the investor route rather than changed globally.
 */
function FounderStoryLegacyRedirect() {
  const location = useLocation();
  return <Navigate to={`/investors/september-2026${location.search}${location.hash}`} replace />;
}

function AppShell({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const isInvestorDeck =
    location.pathname.startsWith("/new-home") ||
    location.pathname.startsWith("/book-a-demo") ||
    location.pathname.startsWith("/start-now") ||
    location.pathname.startsWith("/marketplace-preview") ||
    location.pathname.startsWith("/investors/founder-story") ||
    location.pathname.startsWith("/investors/september-2026");
  const isCrmShell = location.pathname.startsWith("/crm");
  return (
    <div
      className={`app-shell min-h-[100dvh] w-full ${isCrmShell ? "h-[100dvh] overflow-hidden" : isInvestorDeck ? "overflow-x-clip" : "overflow-x-hidden"}`}
    >
      {children}
    </div>
  );
}

function App() {
  useEffect(() => {
    console.log("🚀 Spectra Salon App Started");
  }, []);

  return (
    <StrictMode>
      <BrowserRouter>
        <NotificationProvider>
          <ToastProvider>
            <PageTracker />
            <ErrorBoundary>
              <AppShell>
              <Routes>
                <Route path="/" element={<Frame />} />
                <Route
                  path="/new-home"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-[100dvh] w-full flex items-center justify-center bg-[#F6F2EA]">
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <SalonAiHomePage />
                    </Suspense>
                  }
                />
                <Route
                  path="/book-a-demo"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-[100dvh] w-full flex items-center justify-center bg-[#F6F2EA]">
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <DemoBookingPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/start-now"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-[100dvh] w-full flex items-center justify-center bg-[#F6F2EA]">
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <StartNowPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/marketplace-preview"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-[100dvh] w-full flex items-center justify-center bg-[#F6F2EA]">
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <MarketplaceEditorialPreviewPage />
                    </Suspense>
                  }
                />
                <Route path="/user-login" element={<UserLoginPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/ugc-offer" element={<UGCOfferPage />} />
                <Route path="/salon-performance" element={<Navigate to="/crm/analytics" replace />} />
                {/*
                  Single provider mount for the whole CRM: `/crm/setup` and the
                  main shell share ONE bootstrap so onboarding never triggers an
                  extra cold boot or shell flash. The shell layout (CrmShell)
                  gates sidebar/Outlet on bootstrap success.
                */}
                <Route
                  path="/crm"
                  element={
                    <RequireSalonSession>
                      <CrmProvidersLayout />
                    </RequireSalonSession>
                  }
                >
                  <Route path="setup" element={<FirstRunSetupPage />} />
                  <Route element={<CrmShell />}>
                    <Route index element={<Navigate to="/crm/home" replace />} />
                    <Route path="home" element={<HomeDashboardPage />} />
                    <Route path="front-desk" element={<ReceptionFrontDeskPage />} />
                    <Route path="schedule" element={<SchedulePage />} />
                    <Route path="new-calendar-design" element={<NewCalendarDesignPage />} />
                    <Route path="marketing" element={<MarketingPage />} />
                    {import.meta.env.DEV && <Route path="marketing/dev" element={<MarketingDevPage />} />}
                    <Route path="new-page" element={<Navigate to="/crm/marketing" replace />} />
                    <Route path="customers" element={<CustomersPage />} />
                    <Route path="inventory" element={<InventoryPage />} />
                    <Route path="product-catalog-setup" element={<ProductCatalogSetupPage />} />
                    <Route path="brands/truss" element={<Navigate to="/admin?domain=data&tab=truss" replace />} />
                    <Route path="staff" element={<Navigate to={UNIFIED_TEAM_SETTINGS_PATH} replace />} />
                    <Route path="analytics" element={<SalonPerformanceDashboard embedded />} />
                  </Route>
                </Route>
                <Route path="/market-intelligence" element={<MarketIntelligencePage />} />
                <Route path="/new-investors-deck" element={<NewInvestorsDeckV1 />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/product-database" element={<ProductDatabasePage />} />
                <Route path="/admin/product-resolution" element={<ProductResolutionPage />} />
                <Route path="/admin/usage-intelligence" element={<CustomerUsageIntelligencePage />} />
                <Route path="/admin/usage-intelligence/report/:reportId" element={<CustomerUsageIntelligencePage />} />
                <Route path="/admin/paul-usage-findings" element={<PaulUsageFindingsPage />} />
                {/* Hidden route — direct URL only, not linked from nav */}
                <Route path="/hidden/product-resolution" element={<ProductResolutionPage />} />
                <Route
                  path="/spectra-product-vision"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-[100dvh] w-full flex items-center justify-center bg-black">
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <SpectraProductVisionPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/spectra-product-vision/assets-check"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-[100dvh] w-full flex items-center justify-center bg-black">
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <SpectraAssetCheckPage />
                    </Suspense>
                  }
                />
                {/* Hidden investor experience — direct URL only, not linked from nav */}
                <Route
                  path="/investors/salon-ai"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-[100dvh] w-full flex items-center justify-center" style={{ background: "#F8F7F4" }}>
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <InvestorExperiencePage />
                    </Suspense>
                  }
                />
                {/* Salon AI-first investor narrative — hidden, also in Hidden Pages menu */}
                <Route
                  path="/investors/new-narrative-salon-ai-first"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-[100dvh] w-full flex items-center justify-center" style={{ background: "#F4EEE6" }}>
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <NewNarrativeSalonAIFirstPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/investors/new-narrative-salon-ai-first/working"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-[100dvh] w-full flex items-center justify-center" style={{ background: "#F4EEE6" }}>
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <NewNarrativeSalonAIFirstWorkingPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/investors/new-narrative-salon-ai-first/live-demo-draft"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-[100dvh] w-full flex items-center justify-center" style={{ background: "#F4EEE6" }}>
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <NewNarrativeSalonAIFirstLiveDemoDraftPage />
                    </Suspense>
                  }
                />
                {/* Existing-investor update — private, direct URL only */}
                <Route
                  path="/investors/2026-update"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-[100dvh] w-full flex items-center justify-center" style={{ background: "#17110D" }}>
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <CurrentInvestorUpdatePage />
                    </Suspense>
                  }
                />
                <Route
                  path="/investors/founder-story"
                  element={<FounderStoryLegacyRedirect />}
                />
                <Route
                  path="/investors/september-2026"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-[100dvh] w-full flex items-center justify-center" style={{ background: "#f5efe7" }}>
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <FounderStoryPresentationPage variantId="september2026" />
                    </Suspense>
                  }
                />
                <Route
                  path="/investors/founder-story-short"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-[100dvh] w-full flex items-center justify-center" style={{ background: "#f5efe7" }}>
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <FounderStoryShortPage variantId="september" />
                    </Suspense>
                  }
                />
                {/* Color Intelligence Preview — private manufacturer preview, direct URL only */}
                <Route
                  path="/investors/color-intelligence-preview"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-[100dvh] w-full flex items-center justify-center" style={{ background: "#1F1A15" }}>
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <ColorIntelligencePreviewPage />
                    </Suspense>
                  }
                />
                {/* Israeli customer usage sample — private aggregated example, direct URL only */}
                <Route
                  path="/investors/israel-customer-usage-example"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-[100dvh] w-full flex items-center justify-center" style={{ background: "#1F1A15" }}>
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <IsraelCustomerUsageExamplePage />
                    </Suspense>
                  }
                />
                {/* Advanced salon calendar mockup — standalone product demo, direct URL only */}
                <Route
                  path="/mockups/advanced-booking-calendar"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-[100dvh] w-full flex items-center justify-center" style={{ background: "#1A120C" }}>
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <AdvancedSalonCalendarMockupPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/gtm-research"
                  element={
                    <Suspense
                      fallback={
                        <div className="min-h-[100dvh] w-full flex items-center justify-center bg-[#f7f7f8]">
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <GTMResearchPage />
                    </Suspense>
                  }
                />
                <Route path="/loreal-analytics" element={<LorealAnalyticsPage />} />
                <Route path="/spectra-israel-market-analytics" element={<SpectraIsraelMarketAnalyticsPage />} />
                <Route path="/hairgpt" element={<HairGPTPage />} />
                <Route path="/competitors" element={<CompetitorsPage />} />
                <Route path="/spectra-story" element={<InternalRouteGate><TimelinePage /></InternalRouteGate>} />
                <Route path="/maor-spectra-story" element={<MaorSpectraStoryPage />} />
                <Route
                  path="/strategic-forecast"
                  element={
                    <InternalRouteGate
                      accessCode="1221"
                      sessionKey="strategic_forecast_unlocked"
                      title="Strategic Forecast Access"
                      description="Enter the internal code to unlock the 6-year strategic model."
                    >
                      <StrategicForecastPage />
                    </InternalRouteGate>
                  }
                />
              </Routes>
              </AppShell>
            </ErrorBoundary>
          </ToastProvider>
        </NotificationProvider>
      </BrowserRouter>
    </StrictMode>
  );
}

const root = createRoot(document.getElementById("root")!);
root.render(<App />);
